// What KIND of thing an item is: a boat, an airplane, a vehicle, a home.
//
// This is a LABEL AND AN ICON, and deliberately almost nothing else. The
// product leads with boating, but every feature -- trips, the route map,
// marine weather, tides, places, the trip weather email, tasks, counters,
// sub-equipment, all three logbook types -- stays available to every item of
// every type, INCLUDING a typeless legacy one. See "what this does not gate"
// at the bottom before adding a branch on itemType anywhere.
//
// Shape follows LogbookTypes.mjs (a flat registry plus a lookup that falls back
// rather than throwing) and PlaceFields.mjs (the icon rides ON the registry
// entry, so no consumer keeps a lookup table beside itself).
//
// `TaskPogoItem.itemType` is a plain optional `a.string()`, not an `a.enum()`,
// matching TaskPogoLogbook.logbookType. Consequences worth keeping:
//   - a new type is an entry here and NO schema change, which is the property
//     LogbookTypes.mjs's header calls out for the same reason;
//   - an id this branch doesn't recognize degrades to Unspecified instead of
//     failing serialization of the whole AppSync response. gen2-dev and
//     gen2-prod are independent APIs and prod routinely lags, so an enum member
//     written on dev would be unreadable on prod until prod deployed.
//
// The CDN pin is Font Awesome **6.0.0** (public/index.html), which is older
// than much of what the FA site offers -- a name it doesn't have renders as a
// blank glyph, not an error. Check the version an icon landed in before adding
// one here. (Same trap PlaceFields.mjs documents for FACILITY_FIELDS.)

import { LOGBOOK_TYPE_CAPTAINS, LOGBOOK_TYPE_PILOTS, LOGBOOK_TYPE_MAINTENANCE, logbookTypeById } from './LogbookTypes.mjs';

export const ITEM_TYPE_BOAT = 'boat';
export const ITEM_TYPE_AIRPLANE = 'airplane';
export const ITEM_TYPE_VEHICLE = 'vehicle';
export const ITEM_TYPE_HOME = 'home';
export const ITEM_TYPE_OTHER = 'other';

// Boat leads: it's the headline, and a <select>'s reading order says so.
//
// `ship` rather than `sailboat` for Boat: an item is the user's own vessel and
// the heavier silhouette reads better at list scale. Both names are also used
// by FACILITY_FIELDS (ferry / dinghyDock), but that is a different surface --
// a place's facility column -- so the reuse costs nothing here.
export const ITEM_TYPES = [
  { id: ITEM_TYPE_BOAT,     label: 'Boat',     icon: 'ship' },
  { id: ITEM_TYPE_AIRPLANE, label: 'Airplane', icon: 'plane' },
  { id: ITEM_TYPE_VEHICLE,  label: 'Vehicle',  icon: 'car' },
  { id: ITEM_TYPE_HOME,     label: 'Home',     icon: 'house' },
  { id: ITEM_TYPE_OTHER,    label: 'Other',    icon: 'box-archive' },
];

// Not a member of ITEM_TYPES: "nobody has set this" is not a choice a user
// made, and it must never appear as one in a list of things they could pick.
// It is distinct from `other`, which IS a choice -- "I have said this is not a
// boat, plane, vehicle or home".
//
// `cube` is what every item renders today, so a legacy row looks EXACTLY as it
// does now. That is the whole zero-migration guarantee: no backfill script, no
// data change, nothing written until a user picks something.
export const UNSPECIFIED_ITEM_TYPE = { id: null, label: 'Unspecified', icon: 'cube' };

// Falls back to Unspecified -- NOT to Boat. A fleet list that silently claimed
// an untyped item was a boat would be worse than a neutral glyph, and this app
// is explicitly still for airplanes, homes and cars.
export function itemTypeById(id) {
  return ITEM_TYPES.find((t) => t.id === id) || UNSPECIFIED_ITEM_TYPE;
}

export function itemTypeLabel(id) {
  return itemTypeById(id).label;
}

// The full class string, built in one place so no caller concatenates it.
export function itemTypeIcon(id) {
  return `fa-solid fa-${itemTypeById(id).icon}`;
}

// The option list EditForm's `select` branch wants: {id, index, name}.
//
// The leading id-null option is MANDATORY, not cosmetic. EditForm renders the
// select with `defaultValue={selectValueFromId(field, value)}`, and that helper
// returns undefined for a value it can't find -- so without a matching option a
// legacy null would make React display the FIRST type as though the user had
// chosen it, while nothing was actually stored. Same guard, same reason, as the
// `(None)` row the counter pickers unshift (Common.mjs, Task.jsx).
//
// `index` is unique because EditForm uses it as the React key.
export function itemTypeSelects() {
  return [UNSPECIFIED_ITEM_TYPE, ...ITEM_TYPES].map((t, index) => ({
    id: t.id,
    index,
    name: t.label,
  }));
}

// Which logbook the Add-logbook modal OPENS ON for an item of this type.
//
// A preselected dropdown value in a modal that still lists all three types --
// a suggestion, not a gate. It lives here rather than in LogbookTypes.mjs so
// that module goes on knowing nothing about items.
//
// Boat suggests the Captain's Log rather than the Maintenance Log because the
// app already funnels you into a maintenance log from "complete a task", while
// the Captain's Log has to be discovered.
//
// Routed through logbookTypeById so a bad mapping degrades to Maintenance
// instead of seeding the modal with an id no <option> carries.
export function suggestedLogbookType(itemType) {
  switch (itemType) {
    case ITEM_TYPE_BOAT:     return logbookTypeById(LOGBOOK_TYPE_CAPTAINS).id;
    case ITEM_TYPE_AIRPLANE: return logbookTypeById(LOGBOOK_TYPE_PILOTS).id;
    default:                 return logbookTypeById(LOGBOOK_TYPE_MAINTENANCE).id;
  }
}

// ---------------------------------------------------------------------------
// WHAT THIS DELIBERATELY DOES NOT GATE. Non-boat items are first-class; the
// type changes what an item is CALLED and DRAWN AS, never what it can do.
//
//   - Trips, the route map, weather, tides/currents, places and the trip
//     weather email stay available to every type and to typeless items.
//     TripsSummary stays unconditionally rendered on the item page. An
//     airplane owner filing legs is a real user, and AllTrips already filters
//     on isTripLogbookType -- a LOGBOOK property, which is the right axis.
//   - Every logbook type stays selectable for every item type. Do not filter
//     LOGBOOK_TYPES by itemType: a Pilot's Logbook on a "Vehicle" item is
//     somebody's rally-car stage log.
//   - Counters, tasks, sub-equipment, attachments and email subscriptions are
//     untouched. Non-boat first-class means the SHAPE of an item does not
//     change with its type.
//   - No create-time type picker: creation still writes {name} and opens the
//     edit modal, where Type is simply another row.
//   - No per-type default counters or tasks. Auto-creating an "Engine Hours"
//     counter for a boat would be a write the user never asked for, and
//     opening a page must never schedule a save.
//
// Feature gating DOES exist now -- on `TaskPogoItem.tracking` (a log-only
// item hides its maintenance surfaces), owned by ItemTracking.mjs. That is a
// SEPARATE, explicit user choice about scope, not a property of the type:
// itemType continues to gate nothing, and a branch on itemType is still
// wrong wherever a branch on isLogOnly() would be right.
// ---------------------------------------------------------------------------
